import React, { useState, useMemo } from 'react';
import { ICON_LIBRARY } from '../lib/iconLibrary';
import { Input } from './ui/input';
import { Search } from 'lucide-react';

/**
 * A visual picker for the built-in Lucide icon library.
 * When the user clicks an icon, calls onSelect("lucide:calendar") etc.
 */
export default function IconLibraryPicker({ selected, onSelect }) {
  const [search, setSearch] = useState('');

  // Group icons for a nicer visual browse experience
  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? ICON_LIBRARY.filter(i =>
          i.label.toLowerCase().includes(term) ||
          i.key.toLowerCase().includes(term) ||
          i.group.toLowerCase().includes(term)
        )
      : ICON_LIBRARY;
    const map = {};
    for (const i of filtered) {
      if (!map[i.group]) map[i.group] = [];
      map[i.group].push(i);
    }
    return map;
  }, [search]);

  return (
    <div className="space-y-3" data-testid="icon-library-picker">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          data-testid="icon-library-search"
          placeholder="Search icons..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 h-9 text-sm"
        />
      </div>

      <div className="max-h-72 overflow-y-auto pr-1 space-y-4">
        {Object.entries(grouped).map(([group, icons]) => (
          <div key={group}>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{group}</p>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {icons.map(({ key, label, component: Icon }) => {
                const value = `lucide:${key}`;
                const isSelected = selected === value;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`icon-library-${key}`}
                    onClick={() => onSelect(value)}
                    title={label}
                    className={`aspect-square flex flex-col items-center justify-center gap-1 rounded-lg border transition-all
                      ${isSelected
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                        : 'border-slate-200 bg-white hover:border-blue-400 hover:bg-slate-50'}`}
                  >
                    <Icon className="w-5 h-5 text-slate-700" strokeWidth={1.5} />
                    <span className="text-[10px] text-slate-500 truncate max-w-full px-1">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {Object.keys(grouped).length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">No icons match &ldquo;{search}&rdquo;</p>
        )}
      </div>
    </div>
  );
}
